import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { logoutApi } from "@/api/auth";
import { isNetworkError, isOnline } from "@/offline/network";

// On the web, the session lives in HttpOnly cookies that only the server's
// /auth/logout response can clear. Signing out offline can't reach the
// server, so the cookies would stay valid and the next reload would sign the
// user straight back in. This flag remembers that the sign-out still has to
// reach the server, and keeps the app signed out until it does.
const PENDING_LOGOUT_KEY = "sendstats:pendingLogout";

export function markLogoutPending(): void {
  try {
    localStorage.setItem(PENDING_LOGOUT_KEY, "1");
  } catch (err) {
    Sentry.captureException(err);
  }
}

export function hasPendingLogout(): boolean {
  try {
    return localStorage.getItem(PENDING_LOGOUT_KEY) !== null;
  } catch {
    return false; // storage unavailable: nothing could have been recorded
  }
}

export function clearPendingLogout(): void {
  try {
    localStorage.removeItem(PENDING_LOGOUT_KEY);
  } catch (err) {
    Sentry.captureException(err);
  }
}

/**
 * Send a sign-out that couldn't reach the server earlier, if one is pending
 * and the device is online. A 401 counts as done: the session is already dead.
 */
export async function finishPendingLogout(): Promise<void> {
  if (!hasPendingLogout() || !(await isOnline())) return;
  try {
    await logoutApi();
    clearPendingLogout();
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 401) {
      clearPendingLogout();
    } else if (!isNetworkError(err)) {
      Sentry.captureException(err);
    }
  }
}
